import { useState } from 'react';
import { getImageUrl } from '@/utils/imageUrl';
import { cn } from '@/lib/utils';
import posMaterialImg from '@/images/POS-material.png';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Spinner } from '@/components/ui/spinner';

interface ItemTypeDisplayProps {
  imageUrl?: string | null;
  hexCode?: string | null;
  alt?: string;
  className?: string;
  showLargeImageOnHover?: boolean;
}
export function ItemTypeDisplay({
  imageUrl,
  hexCode,
  alt = 'Item type',
  className,
  showLargeImageOnHover,
}: ItemTypeDisplayProps) {
  const imgSmall = imageUrl ? getImageUrl(48, imageUrl) : posMaterialImg;
  const display = (
    <div
      className={cn(
        'tw:flex tw:items-center tw:rounded tw:shadow-sm tw:size-12 tw:rounded-l tw:overflow-hidden',
        showLargeImageOnHover && 'tw:cursor-help',
        className,
      )}
    >
      {/* Colored vertical bar on the left */}
      <div
        className="tw:w-[14%] tw:h-full"
        style={{
          backgroundColor: hexCode ? `#${hexCode}` : 'hsl(0, 0%, 60%)',
        }}
      />
      {/* Container/Box image — or letter fallback for items without an image */}
      <div className="tw:relative tw:h-full tw:flex-1 tw:bg-white">
        <img
          alt={alt}
          className="tw:absolute tw:inset-0 tw:max-w-none tw:object-contain tw:pointer-events-none tw:size-full"
          src={imgSmall}
        />
      </div>
    </div>
  );

  if (!showLargeImageOnHover) return display;

  const imgLarge = imageUrl ? getImageUrl(512, imageUrl) : posMaterialImg;

  return (
    <HoverCard openDelay={300} closeDelay={0}>
      <HoverCardTrigger asChild>{display}</HoverCardTrigger>
      <HoverCardContent className="tw:w-auto tw:p-0 tw:rounded-l tw:overflow-hidden">
        <HoverImage src={imgLarge} hexCode={hexCode} alt={alt} />
      </HoverCardContent>
    </HoverCard>
  );
}

function HoverImage({
  src,
  hexCode,
  alt,
}: {
  src: string;
  hexCode: string | null | undefined;
  alt: string;
}) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="tw:relative tw:size-54">
      {!loaded && (
        <div className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center">
          <Spinner className="tw:size-6" />
        </div>
      )}
      <img
        alt={alt}
        className={cn(
          'tw:w-full tw:h-full tw:object-contain tw:transition-opacity',
          loaded ? 'tw:opacity-100' : 'tw:opacity-0',
        )}
        src={src}
        onLoad={() => setLoaded(true)}
      />
      {hexCode && (
        <div
          className="tw:absolute tw:inset-0 tw:w-[12%] tw:h-full"
          style={{
            backgroundColor: `#${hexCode}`,
          }}
        />
      )}
    </div>
  );
}
